import { Info } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader } from "@/components/ui/table";
import { NestedDrawerTableHeaderRow, NestedDrawerTableBodyRow } from "@/components/shared/NestedDrawerTable";
import { ProductTableCell } from "@/components/shared/ProductTableCell";
import { Badge } from "@/components/ui/badge";
import { BI_TOOLTIPS } from "./tooltips";
import { useGetBrandIntelligenceKeywordHistory } from "@/resources/brand-intelligence/brand-intelligence.api";

// "2026-08" -> "Aug 26" for the 12-month contribution column headers (matches the prototype).
const MONTH_ABBR = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const fmtMonthLabel = (m: string) => {
  const [y, mo] = m.split("-");
  return `${MONTH_ABBR[Number(mo) - 1] ?? mo} ${y.slice(2)}`;
};

// Prototype's discrete 7-step heat scale (strongest share = deep green → weakest = deep red),
// with contrast-aware text. The app has no --heat-* tokens, so the clean stops are inlined here
// instead of a continuous hue sweep (which muddied the mid-band into olive).
const HEAT_STEPS: readonly { bg: string; fg: string }[] = [
  { bg: "hsl(142 64% 33%)", fg: "hsl(0 0% 100%)" }, // 1 strongest — deep green
  { bg: "hsl(136 50% 45%)", fg: "hsl(0 0% 100%)" }, // 2 — green
  { bg: "hsl(98 46% 68%)", fg: "hsl(120 35% 18%)" }, // 3 — light green
  { bg: "hsl(46 92% 68%)", fg: "hsl(40 55% 20%)" }, // 4 mid — amber
  { bg: "hsl(28 88% 64%)", fg: "hsl(24 60% 20%)" }, // 5 — orange
  { bg: "hsl(12 74% 53%)", fg: "hsl(0 0% 100%)" }, // 6 — red-orange
  { bg: "hsl(2 70% 44%)", fg: "hsl(0 0% 100%)" }, // 7 weakest — deep red
];
function heatCell(v: number | null, max: number): React.CSSProperties {
  if (v == null) return { backgroundColor: "hsl(var(--muted))", color: "hsl(var(--muted-foreground))" };
  const t = max > 0 ? Math.min(1, Math.max(0, v / max)) : 0; // 0 (weak) -> 1 (strong)
  const step = 6 - Math.min(6, Math.floor(t * 7)); // t=1 -> 0 (green), t=0 -> 6 (red)
  return { backgroundColor: HEAT_STEPS[step].bg, color: HEAT_STEPS[step].fg };
}

// Section 5.3 + 5.4: per-product 12-month Keyword SOV contribution. One row per contributing
// product, each with its monthly SOV points aligned to a shared month axis (latest month = the
// current §5.3 contribution). Reads the materialized per-product history -- no live scan.
export function KeywordProductHistory({ brandId, keyword }: { brandId: number; keyword: string }) {
  const { data, isLoading } = useGetBrandIntelligenceKeywordHistory(brandId, keyword);
  if (isLoading) return <div className="py-6 text-center text-sm text-muted-foreground">Loading 12-month product contribution…</div>;
  if (!data || data.products.length === 0) {
    return (
      <div className="py-6 text-center text-sm text-muted-foreground">
        {data && !data.materialized
          ? "This keyword's 12-month history arrives with the next Refresh."
          : "No products contributing to SOV for this keyword yet."}
      </div>
    );
  }
  const months = data.months;
  const max = Math.max(0.1, ...data.products.flatMap((p) => p.points.map((pt) => pt.product_sov ?? 0)));
  const productColWidth = 260;
  const subColWidth = 180;
  const monthColWidth = 56;
  const tableWidth = productColWidth + subColWidth + months.length * monthColWidth;
  return (
    <div className="py-4 space-y-3">
      <div className="px-4">
        <p className="flex items-center gap-1 text-sm font-semibold text-foreground">
          12-month SOV contribution
          <span title={BI_TOOLTIPS.contributionToKeywordSov}><Info className="h-3 w-3 text-muted-foreground" /></span>
        </p>
        <p className="text-xs text-muted-foreground mt-0.5">
          SOV points each product contributed to “{keyword}” over the last 12 months. Green = stronger share, amber = mid, red = weaker.
        </p>
      </div>
      <Table stickyHeader={false} className="table-fixed w-full" style={{ minWidth: tableWidth }}>
        <colgroup>
          <col style={{ width: productColWidth }} />
          <col style={{ width: subColWidth }} />
          {months.map((m) => <col key={m} style={{ width: monthColWidth }} />)}
        </colgroup>
        <TableHeader>
          <NestedDrawerTableHeaderRow flush>
            <TableHead className="pl-4">Product</TableHead>
            <TableHead className="whitespace-nowrap">Sub-category</TableHead>
            {months.map((m) => (
              <TableHead key={m} className="text-center whitespace-nowrap px-1">{fmtMonthLabel(m)}</TableHead>
            ))}
          </NestedDrawerTableHeaderRow>
        </TableHeader>
        <TableBody>
          {data.products.map((p) => (
            <NestedDrawerTableBodyRow key={p.asin} flush className="hover:bg-card">
              <TableCell className="pl-4 align-middle">
                <ProductTableCell name={p.title || p.asin} asin={p.asin} imageUrl={p.image_url || undefined} size="lg" wrapName asinLinkToAmazon />
              </TableCell>
              <TableCell className="align-middle">
                {p.sub_category ? (
                  <Badge variant="secondary" className="text-foreground">{p.sub_category}</Badge>
                ) : (
                  <span className="text-xs text-muted-foreground">—</span>
                )}
              </TableCell>
              {p.points.map((pt) => (
                <TableCell key={pt.month} className="p-1 align-middle">
                  <div className="rounded-sm py-2 text-center text-xs tabular-nums" style={heatCell(pt.product_sov, max)}>
                    {pt.product_sov == null ? "—" : pt.product_sov.toFixed(1)}
                  </div>
                </TableCell>
              ))}
            </NestedDrawerTableBodyRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
